import React, { useState } from 'react';
import { ChevronDown, Plus, Users, Check, Settings, LogOut, Copy, Link, UserPlus, X } from 'lucide-react';
import { useGroup } from '../../contexts/GroupContext';

const GroupSwitcher = () => {
  const {
    currentGroup,
    userGroups,
    isAdmin,
    switchGroup,
    createGroup,
    createInvite,
    leaveGroup,
    loading
  } = useGroup();

  const [isOpen, setIsOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [inviteLink, setInviteLink] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;

    setCreating(true);
    setError('');
    try {
      await createGroup(newGroupName.trim());
      setNewGroupName('');
      setShowCreateModal(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleCreateInvite = async () => {
    setError('');
    try {
      const invite = await createInvite(inviteEmail || null);
      setInviteLink(invite);
      setInviteEmail('');
    } catch (err) {
      setError(err.message);
    }
  };

  const copyInviteLink = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink.link);
    }
  };

  const handleLeaveGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to leave this group? You will need a new invite to rejoin.')) {
      return;
    }
    try {
      await leaveGroup(groupId);
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) {
    return (
      <div className="h-10 w-40 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
    );
  }

  // No groups - show create button
  if (!currentGroup) {
    return (
      <>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={20} />
          Create Band
        </button>

        {/* Create Modal */}
        {showCreateModal && (
          <CreateGroupModal
            show={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            newGroupName={newGroupName}
            setNewGroupName={setNewGroupName}
            handleCreateGroup={handleCreateGroup}
            creating={creating}
            error={error}
          />
        )}
      </>
    );
  }

  return (
    <>
      {/* Group Switcher Dropdown */}
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors min-w-[160px]"
        >
          <Users size={18} className="text-gray-600 dark:text-gray-400" />
          <span className="font-medium text-gray-800 dark:text-gray-100 truncate max-w-[120px]">
            {currentGroup.name}
          </span>
          <ChevronDown size={18} className={`text-gray-600 dark:text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />

            {/* Dropdown */}
            <div className="absolute top-full left-0 mt-2 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden">
              {/* Groups List */}
              <div className="max-h-64 overflow-y-auto">
                {userGroups.map((group) => (
                  <button
                    key={group.id}
                    onClick={() => {
                      switchGroup(group.id);
                      setIsOpen(false);
                    }}
                    className={`w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                      currentGroup.id === group.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-bold text-sm">
                        {group.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-gray-800 dark:text-gray-100">{group.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {Object.keys(group.members).length} member{Object.keys(group.members).length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    {currentGroup.id === group.id && (
                      <Check size={18} className="text-blue-600 dark:text-blue-400" />
                    )}
                  </button>
                ))}
              </div>

              {/* Actions */}
              <div className="border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => {
                    setShowCreateModal(true);
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                >
                  <Plus size={18} />
                  <span>Create New Band</span>
                </button>

                {isAdmin && (
                  <button
                    onClick={() => {
                      setShowInviteModal(true);
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                  >
                    <UserPlus size={18} />
                    <span>Invite Members</span>
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Create Group Modal */}
      {showCreateModal && (
        <CreateGroupModal
          show={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          newGroupName={newGroupName}
          setNewGroupName={setNewGroupName}
          handleCreateGroup={handleCreateGroup}
          creating={creating}
          error={error}
        />
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <InviteModal
          show={showInviteModal}
          onClose={() => {
            setShowInviteModal(false);
            setInviteLink(null);
            setInviteEmail('');
            setError('');
          }}
          inviteEmail={inviteEmail}
          setInviteEmail={setInviteEmail}
          inviteLink={inviteLink}
          handleCreateInvite={handleCreateInvite}
          copyInviteLink={copyInviteLink}
          error={error}
          groupName={currentGroup.name}
        />
      )}
    </>
  );
};

// Create Group Modal Component
const CreateGroupModal = ({ show, onClose, newGroupName, setNewGroupName, handleCreateGroup, creating, error }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          <X size={20} />
        </button>

        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">Create a Band</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Start a new band profile to track shows, contacts, and finances.
        </p>

        <form onSubmit={handleCreateGroup}>
          <input
            type="text"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            placeholder="Band name"
            className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:border-blue-500 text-lg"
            autoFocus
          />

          {error && (
            <p className="mt-2 text-red-600 dark:text-red-400 text-sm">{error}</p>
          )}

          <button
            type="submit"
            disabled={creating || !newGroupName.trim()}
            className="w-full mt-4 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {creating ? 'Creating...' : 'Create Band'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Invite Modal Component
const InviteModal = ({ show, onClose, inviteEmail, setInviteEmail, inviteLink, handleCreateInvite, copyInviteLink, error, groupName }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    copyInviteLink();
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          <X size={20} />
        </button>

        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">Invite to {groupName}</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Create an invite link to share with band members.
        </p>

        {!inviteLink ? (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email (optional)
              </label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="member@email.com"
                className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                If specified, only this email can use the invite.
              </p>
            </div>

            {error && (
              <p className="mb-4 text-red-600 dark:text-red-400 text-sm">{error}</p>
            )}

            <button
              onClick={handleCreateInvite}
              className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <Link size={20} />
              Generate Invite Link
            </button>
          </>
        ) : (
          <div className="space-y-4">
            <div className="bg-gray-100 dark:bg-gray-700 rounded-xl p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Invite Link</p>
              <p className="text-sm text-gray-800 dark:text-gray-100 break-all font-mono">
                {inviteLink.link}
              </p>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
              <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">Invite Code</p>
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-300 tracking-wider">
                {inviteLink.code}
              </p>
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              This invite expires in 7 days
            </p>

            <button
              onClick={handleCopy}
              className="w-full py-3 bg-gray-800 dark:bg-gray-600 text-white rounded-xl font-semibold hover:bg-gray-900 dark:hover:bg-gray-500 transition-colors flex items-center justify-center gap-2"
            >
              {copied ? (
                <>
                  <Check size={20} />
                  Copied!
                </>
              ) : (
                <>
                  <Copy size={20} />
                  Copy Link
                </>
              )}
            </button>

            <button
              onClick={() => {
                setInviteEmail('');
                handleCreateInvite();
              }}
              className="w-full py-3 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors"
            >
              Generate Another
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupSwitcher;